import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import {
  FaTimes, FaDownload, FaExternalLinkAlt, FaPen, FaLink, FaUserPlus,
  FaUsers, FaClone, FaTrash, FaInfoCircle, FaEdit, FaGoogleDrive,
} from 'react-icons/fa';
import { getFileMeta, getExtension } from './fileTypes';
import { getSize } from 'helpers/utils';
import { ONLYOFFICE_SUPPORTED_TYPES, ONLYOFFICE_EDITABLE_TYPES } from 'components/app/drive/hooks/useFileIcon';

const ActionBtn = ({ icon: Icon, title, onClick, danger = false }) => (
  <button
    type="button"
    onClick={onClick}
    className={[
      'tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded-md tw-border tw-cursor-pointer tw-transition-all tw-bg-slate-800 tw-border-slate-700',
      danger
        ? 'tw-text-red-300 hover:tw-bg-red-600 hover:tw-text-white hover:tw-border-red-600'
        : 'tw-text-slate-300 hover:tw-bg-white hover:tw-text-black hover:tw-border-white',
    ].join(' ')}
    title={title}
    aria-label={title}
  >
    <Icon size={11} />
  </button>
);

ActionBtn.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  danger: PropTypes.bool,
};

const DrivePreviewModal = ({ asset, onClose, handleAction }) => {
  useEffect(() => {
    if (!asset) return undefined;
    const handleEsc = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', handleEsc);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = prev;
    };
  }, [asset, onClose]);

  if (!asset) return null;

  const { kind, icon: Icon, color } = getFileMeta(asset);
  const url = asset.preview_url || '';
  const ext = getExtension(asset.name);
  const canOpenInOnlyOffice = ONLYOFFICE_SUPPORTED_TYPES.includes(asset.content_type);
  const canEditInOnlyOffice = ONLYOFFICE_EDITABLE_TYPES.includes(asset.content_type);

  const {
    allow_view, allow_edit, allow_copy, allow_share, allow_make_public,
    allow_move_to_trash, allow_download,
  } = asset.permissions || {};

  const isPublic = asset.public;

  const fire = (action) => {
    onClose();
    handleAction(asset, action);
  };

  const renderBody = () => {
    switch (kind) {
      case 'image':
        return (
          <div className="tw-flex-1 tw-min-h-0 tw-flex tw-items-center tw-justify-center tw-bg-slate-950 tw-overflow-hidden">
            <img src={url} alt={asset.name} className="tw-block tw-object-contain tw-max-w-full tw-max-h-full" />
          </div>
        );
      case 'pdf':
        return (
          <div className="tw-flex-1 tw-min-h-0 tw-bg-slate-100">
            <iframe title={asset.name} src={url} className="tw-w-full tw-h-full tw-border-0 tw-block" />
          </div>
        );
      case 'video':
        return (
          <div className="tw-flex-1 tw-min-h-0 tw-bg-black tw-flex tw-items-center tw-justify-center">
            <video controls autoPlay className="tw-max-w-full tw-max-h-full" src={url}>
              <source src={url} type={asset.content_type} />
            </video>
          </div>
        );
      case 'audio':
        return (
          <div className="tw-flex-1 tw-min-h-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-bg-slate-900 tw-p-8">
            <div className="tw-w-20 tw-h-20 tw-bg-slate-800 tw-rounded-[999px] tw-flex tw-items-center tw-justify-center">
              <Icon size={32} style={{ color }} />
            </div>
            <audio controls autoPlay src={url} className="tw-w-full tw-max-w-md">
              <source src={url} type={asset.content_type} />
            </audio>
          </div>
        );
      default:
        return (
          <div className="tw-flex-1 tw-min-h-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-p-8 tw-bg-slate-900">
            <div className="tw-w-24 tw-h-24 tw-bg-slate-800 tw-rounded-2xl tw-flex tw-items-center tw-justify-center tw-mb-5 tw-border tw-border-slate-700">
              <Icon size={44} style={{ color }} />
            </div>
            <h3 className="tw-text-base tw-font-bold tw-text-white tw-mb-1 tw-max-w-md tw-truncate">{asset.name}</h3>
            <p className="tw-text-slate-400 tw-text-xs tw-m-0">In-browser preview not available.</p>
            <p className="tw-text-slate-500 tw-text-[11px] tw-font-mono tw-mt-1 tw-mb-6 tw-uppercase">{ext} · {getSize(asset.file_size || 0)}</p>
            {canOpenInOnlyOffice && (
              <button
                type="button"
                onClick={() => fire('view-online')}
                className="tw-px-4 tw-py-2 tw-bg-white tw-text-black tw-font-bold tw-rounded-md hover:tw-bg-slate-200 tw-transition-colors tw-text-xs tw-flex tw-items-center tw-gap-2 tw-border-0 tw-cursor-pointer"
              >
                <FaGoogleDrive /> View online
              </button>
            )}
            <p className="tw-text-slate-600 tw-text-[10px] tw-mt-6 tw-italic">Use the toolbar above to download or manage this file.</p>
          </div>
        );
    }
  };

  const modal = (
    <div
      className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-p-4 sm:tw-p-6"
      style={{ zIndex: 999999, backgroundColor: 'rgba(15,23,42,0.92)' }}
      onClick={onClose}
    >
      <div
        className="tw-flex tw-flex-col tw-bg-slate-950 tw-rounded-lg tw-shadow-2xl tw-border tw-border-slate-800 tw-overflow-hidden"
        style={{ width: 'min(95vw, 1100px)', height: '88vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-2.5 tw-border-b tw-border-slate-800 tw-bg-slate-900 tw-shrink-0 tw-gap-3">
          <div className="tw-flex tw-items-center tw-gap-3 tw-min-w-0 tw-flex-1">
            <div className="tw-w-8 tw-h-8 tw-bg-slate-800 tw-rounded-md tw-flex tw-items-center tw-justify-center tw-shrink-0 tw-border tw-border-slate-700">
              <Icon size={14} style={{ color }} />
            </div>
            <div className="tw-min-w-0">
              <p className="tw-text-xs sm:tw-text-sm tw-font-bold tw-text-white tw-truncate tw-m-0" title={asset.name}>{asset.name}</p>
              <p className="tw-text-[10px] tw-font-mono tw-text-slate-500 tw-uppercase tw-m-0 tw-mt-0.5">{ext || kind} · {getSize(asset.file_size || 0)}{isPublic && ' · shared'}</p>
            </div>
          </div>

          {/* Action toolbar */}
          <div className="tw-flex tw-items-center tw-gap-1 tw-shrink-0">
            {url && (kind === 'image' || kind === 'pdf') && (
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded-md tw-bg-slate-800 tw-border tw-border-slate-700 tw-text-slate-300 hover:tw-bg-white hover:tw-text-black hover:tw-border-white tw-transition-all tw-no-underline"
                title="Open in new tab"
              >
                <FaExternalLinkAlt size={10} />
              </a>
            )}

            {canOpenInOnlyOffice && allow_view && (
              <ActionBtn icon={FaGoogleDrive} title="View online" onClick={() => fire('view-online')} />
            )}
            {canEditInOnlyOffice && allow_edit && (
              <ActionBtn icon={FaEdit} title="Edit online" onClick={() => fire('edit-online')} />
            )}
            <ActionBtn icon={FaLink} title="Get link" onClick={() => fire('getLink')} />
            {!isPublic && allow_share       && <ActionBtn icon={FaUserPlus} title="Share"       onClick={() => fire('share')} />}
            {!isPublic && allow_make_public && <ActionBtn icon={FaUsers}    title="Make public" onClick={() => fire('makePublic')} />}
            {allow_edit                     && <ActionBtn icon={FaPen}      title="Rename"      onClick={() => fire('rename')} />}
            {allow_copy && kind !== 'folder' && <ActionBtn icon={FaClone}   title="Make a copy" onClick={() => fire('copy')} />}
            <ActionBtn icon={FaInfoCircle} title="Details" onClick={() => fire('detail')} />
            {allow_download && <ActionBtn icon={FaDownload} title="Download" onClick={() => fire('download')} />}
            {allow_move_to_trash && <ActionBtn icon={FaTrash} title="Move to trash" danger onClick={() => fire('moveToTrash')} />}

            <div className="tw-mx-1 tw-w-px tw-h-5 tw-bg-slate-700" />

            <button
              type="button"
              onClick={onClose}
              className="tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded-md tw-bg-slate-800 tw-border tw-border-slate-700 tw-text-slate-400 hover:tw-text-white hover:tw-bg-slate-700 tw-transition-all tw-cursor-pointer"
              aria-label="Close"
            >
              <FaTimes size={12} />
            </button>
          </div>
        </div>

        {/* Body */}
        {renderBody()}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

DrivePreviewModal.propTypes = {
  asset: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  handleAction: PropTypes.func.isRequired,
};

export default DrivePreviewModal;
